import React, { useState, useEffect, useCallback } from 'react';
import {
  GraduationCap,
  Calendar,
  Clock,
  MapPin,
  Video,
  Users,
  ChevronRight,
  CheckCircle,
  ExternalLink,
  Loader,
  AlertCircle
} from 'lucide-react';
import {
  addDays,
  endOfDay,
  format,
  isToday,
  isTomorrow,
  isValid,
  parseISO,
  startOfDay
} from 'date-fns';
import api from '../../utils/api';

export const getClassStart = (event) => {
  if (!event?.startTime) {
    return null;
  }

  const value = event.date && !event.startTime.includes('T')
    ? `${event.date}T${event.startTime}`
    : event.startTime;
  const parsed = parseISO(value);

  return isValid(parsed) ? parsed : null;
};

const getClassTitle = (event) => {
  const className = event.class?.name || '';

  if (/^Session \d+\s*:/i.test(className)) {
    return 'Activation week class';
  }

  return event.title || className || event.eventName || 'Class';
};

const getInstructorName = (event) => {
  if (typeof event.instructor === 'string') {
    return event.instructor;
  }

  if (event.instructor) {
    return [event.instructor.firstName, event.instructor.lastName]
      .filter(Boolean)
      .join(' ');
  }

  return event.instructorName || '';
};

/**
 * UpcomingEducation - Compact view of upcoming education events
 * Shows classes in the next seven days with quick registration
 */
const UpcomingEducation = ({ onViewAllClasses }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingEventId, setProcessingEventId] = useState(null);

  // Fetch upcoming events
  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const now = new Date();
      const rangeStart = startOfDay(now);
      const rangeEnd = endOfDay(addDays(rangeStart, 6));
      const response = await api.get('/classEvents/agent/weekly', {
        params: {
          weekStart: format(rangeStart, 'yyyy-MM-dd')
        }
      });

      if (response.data.success) {
        const upcomingEvents = (response.data.data || [])
          .filter(event => {
            const eventStart = getClassStart(event);
            return eventStart && eventStart >= now && eventStart <= rangeEnd;
          })
          .sort((a, b) => getClassStart(a) - getClassStart(b));

        setEvents(upcomingEvents);
      } else {
        setError('Failed to load classes');
      }
    } catch (err) {
      console.error('Error fetching events:', err);
      setError(err.response?.data?.message || 'Failed to load classes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Handle registration
  const handleRegister = async (eventId) => {
    setProcessingEventId(eventId);
    try {
      const response = await api.post('/classEvents/agent/register', { eventId });

      if (response.data.success) {
        setEvents(prevEvents =>
          prevEvents.map(event =>
            event._id === eventId
              ? {
                  ...event,
                  isAgentRegistered: true,
                  agentRegistrationStatus: response.data.requiresExternalRegistration ? 'pre-registered' : 'registered'
                }
              : event
          )
        );

        if (response.data.requiresExternalRegistration && response.data.externalRegistrationLink) {
          window.open(response.data.externalRegistrationLink, '_blank');
        }
      }
    } catch (err) {
      console.error('Registration error:', err);
      alert(err.response?.data?.message || 'Failed to register for class');
    } finally {
      setProcessingEventId(null);
    }
  };

  // Format the event date for display
  const formatEventDate = (event) => {
    const eventDate = getClassStart(event);

    if (isToday(eventDate)) {
      return 'Today';
    }
    if (isTomorrow(eventDate)) {
      return 'Tomorrow';
    }
    return format(eventDate, 'EEE, MMM d');
  };

  // Format time
  const formatTime = (event) => {
    return format(getClassStart(event), 'h:mm a');
  };

  // Get event type styling
  const getEventTypeStyles = (eventType) => {
    switch (eventType) {
      case 'webinar':
        return { bg: 'bg-purple-100', text: 'text-purple-600', icon: Video };
      case 'workshop':
        return { bg: 'bg-blue-100', text: 'text-blue-600', icon: Users };
      case 'training':
        return { bg: 'bg-green-100', text: 'text-green-600', icon: GraduationCap };
      default:
        return { bg: 'bg-gray-100', text: 'text-gray-600', icon: Calendar };
    }
  };

  return (
    <div id="upcoming-education-section" className="bg-white rounded-xl shadow-sm border border-gray-100">
      <div id="education-header" className="p-4 border-b border-gray-200 sm:p-6">
        <div
          id="education-header-layout"
          className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between"
        >
          <div id="education-header-content" className="flex min-w-0 items-center">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
              <GraduationCap className="w-5 h-5 text-green-600" />
            </div>
            <div id="education-heading" className="min-w-0">
              <h3 className="text-lg font-semibold text-gray-900">Classes in the next 7 days</h3>
              <p className="text-sm text-gray-500">Register for an upcoming class</p>
            </div>
          </div>
          {onViewAllClasses && (
            <button
              id="view-all-classes-button"
              onClick={onViewAllClasses}
              className="flex flex-shrink-0 items-center text-sm font-medium text-blue-600 hover:text-blue-800"
            >
              View Calendar
              <ChevronRight className="w-4 h-4 ml-1" />
            </button>
          )}
        </div>
      </div>

      <div className="p-6">
        {loading ? (
          <div id="education-loading" className="text-center py-8">
            <Loader className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-3" />
            <p className="text-gray-500 text-sm">Loading classes...</p>
          </div>
        ) : error ? (
          <div id="education-error" className="text-center py-8">
            <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-3" />
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        ) : events.length === 0 ? (
          <div id="no-education-events" className="text-center py-8">
            <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-3">
              <Calendar className="w-6 h-6 text-gray-400" />
            </div>
            <p className="text-gray-600 font-medium mb-1">
              No classes in the next 7 days
            </p>
            <p className="text-sm text-gray-500">
              Check the full calendar for future events
            </p>
          </div>
        ) : (
          <div id="education-events-list" className="space-y-3">
            {events.map((event) => {
              const typeStyles = getEventTypeStyles(event.eventType);
              const TypeIcon = typeStyles.icon;
              const isRegistered = event.isAgentRegistered;
              const isProcessing = processingEventId === event._id;
              const eventDate = getClassStart(event);
              const isTodayEvent = isToday(eventDate);
              const instructorName = getInstructorName(event);

              return (
                <div
                  key={event._id}
                  id={`education-event-${event._id}`}
                  className={`p-4 rounded-lg border transition-all ${
                    isTodayEvent
                      ? 'border-green-200 bg-green-50'
                      : 'border-gray-100 bg-gray-50 hover:border-gray-200'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start flex-1 min-w-0">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${typeStyles.bg}`}>
                        <TypeIcon className={`w-5 h-5 ${typeStyles.text}`} />
                      </div>
                      <div className="ml-3 flex-1 min-w-0">
                        <h4 className="break-words font-medium leading-tight text-gray-900">
                          {getClassTitle(event)}
                        </h4>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-sm text-gray-600">
                          <span className={`font-medium ${isTodayEvent ? 'text-green-600' : ''}`}>
                            {formatEventDate(event)}
                          </span>
                          <span className="flex items-center">
                            <Clock className="w-3.5 h-3.5 mr-1" />
                            {formatTime(event)}
                          </span>
                          {event.location && (
                            <span className="flex items-center">
                              <MapPin className="w-3.5 h-3.5 mr-1" />
                              <span className="truncate max-w-[120px]">{event.location}</span>
                            </span>
                          )}
                        </div>
                        {instructorName && (
                          <p className="text-xs text-gray-500 mt-1">
                            with {instructorName}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="ml-3 flex-shrink-0">
                      {isRegistered ? (
                        <div className="flex items-center text-green-600">
                          <CheckCircle className="w-5 h-5 mr-1" />
                          <span className="text-sm font-medium">Registered</span>
                        </div>
                      ) : (
                        <button
                          id={`register-event-${event._id}`}
                          onClick={() => handleRegister(event._id)}
                          disabled={isProcessing}
                          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                            isProcessing
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-blue-600 text-white hover:bg-blue-700'
                          }`}
                        >
                          {isProcessing ? (
                            <Loader className="w-4 h-4 animate-spin" />
                          ) : (
                            'Register'
                          )}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Join link for registered events happening today */}
                  {isRegistered && isTodayEvent && event.meetingLink && (
                    <div className="mt-3 pt-3 border-t border-green-200">
                      <a
                        href={event.meetingLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-sm font-medium text-green-700 hover:text-green-800"
                      >
                        <Video className="w-4 h-4 mr-2" />
                        Join Class
                        <ExternalLink className="w-3 h-3 ml-1" />
                      </a>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default UpcomingEducation;
